import api, {
  apiRequest,
  getLinks,
  apiRequestGet,
  apiRequestPost,
} from 'flavours/glitch/api';
import type { ApiAccountJSON } from 'flavours/glitch/api_types/accounts';
import type {
  ApiNotificationGroupsResultJSON,
  ApiNotificationRequestJSON,
  ApiNotificationJSON,
  ApiNotificationGroupJSON,
} from 'flavours/glitch/api_types/notifications';
import type { ApiStatusJSON } from 'flavours/glitch/api_types/statuses';
import {
  getInstanceCapabilities,
  setNotificationsV2Support,
} from 'flavours/glitch/features/standalone/initial_state';

// Standalone: some instances (older Mastodon, alternative software) do not
// implement the grouped `/api/v2/notifications` endpoint. We detect that and
// fall back to `/api/v1/notifications`, re-grouping client-side into the v2
// shape the UI expects.
function convertV1ToV2Format(
  notifications: ApiNotificationJSON[],
): ApiNotificationGroupsResultJSON {
  const accounts = new Map<string, ApiAccountJSON>();
  const statuses = new Map<string, ApiStatusJSON>();
  const groups = new Map<string, ApiNotificationGroupJSON>();

  notifications.forEach((notification) => {
    accounts.set(notification.account.id, notification.account);

    let groupKey: string;
    const baseGroup = {
      notifications_count: 1,
      type: notification.type,
      most_recent_notification_id: notification.id,
      page_min_id: notification.id,
      page_max_id: notification.id,
      latest_page_notification_at: notification.created_at,
      sample_account_ids: [notification.account.id],
    };

    if ('status' in notification && notification.status) {
      statuses.set(notification.status.id, notification.status);
      groupKey = `${notification.type}-${notification.status.id}`;

      if (!groups.has(groupKey)) {
        groups.set(groupKey, {
          ...baseGroup,
          group_key: groupKey,
          status_id: notification.status.id,
        } as ApiNotificationGroupJSON);
      } else {
        const group = groups.get(groupKey);
        if (!group) return;

        group.notifications_count++;
        if (!group.sample_account_ids.includes(notification.account.id)) {
          group.sample_account_ids.push(notification.account.id);
        }

        if (notification.created_at > group.latest_page_notification_at) {
          group.latest_page_notification_at = notification.created_at;
          group.page_max_id = notification.id;
          group.most_recent_notification_id = notification.id;
        }
        if (notification.id < (group.page_min_id ?? notification.id)) {
          group.page_min_id = notification.id;
        }
      }
    } else if (
      notification.type === 'follow' ||
      notification.type === 'admin.sign_up'
    ) {
      const tempKey = notification.type;

      if (!groups.has(tempKey)) {
        groupKey = `${notification.type}-${notification.id}`;
        groups.set(tempKey, {
          ...baseGroup,
          group_key: groupKey,
        } as ApiNotificationGroupJSON);
      } else {
        const group = groups.get(tempKey);
        if (!group) return;

        group.notifications_count++;
        if (!group.sample_account_ids.includes(notification.account.id)) {
          group.sample_account_ids.push(notification.account.id);
        }

        if (notification.created_at > group.latest_page_notification_at) {
          group.latest_page_notification_at = notification.created_at;
          group.page_max_id = notification.id;
          group.most_recent_notification_id = notification.id;
        }
        if (notification.id < (group.page_min_id ?? notification.id)) {
          group.page_min_id = notification.id;
        }
      }
    } else {
      groupKey = `ungrouped-${notification.id}`;
      groups.set(groupKey, {
        ...baseGroup,
        group_key: groupKey,
      } as ApiNotificationGroupJSON);
    }
  });

  return {
    accounts: Array.from(accounts.values()),
    statuses: Array.from(statuses.values()),
    notification_groups: Array.from(groups.values()),
  };
}

const fetchV1NotificationGroups = async (params?: {
  grouped_types?: string[];
  exclude_types?: string[];
  max_id?: string;
  since_id?: string;
}) => {
  const v1Response = await api().request<ApiNotificationJSON[]>({
    method: 'GET',
    url: '/api/v1/notifications',
    params: {
      exclude_types: params?.exclude_types,
      max_id: params?.max_id,
      since_id: params?.since_id,
    },
  });

  const v2Format = convertV1ToV2Format(v1Response.data);

  return {
    statuses: v2Format.statuses,
    accounts: v2Format.accounts,
    notifications: v2Format.notification_groups,
    links: getLinks(v1Response),
  };
};

export const apiFetchNotifications = async (
  params?: {
    account_id?: string;
    since_id?: string;
  },
  url?: string,
) => {
  const response = await api().request<ApiNotificationJSON[]>({
    method: 'GET',
    url: url ?? '/api/v1/notifications',
    params,
  });

  return {
    notifications: response.data,
    links: getLinks(response),
  };
};

export const apiFetchNotificationGroups = async (params?: {
  url?: string;
  grouped_types?: string[];
  exclude_types?: string[];
  max_id?: string;
  since_id?: string;
}) => {
  // Standalone: skip straight to v1 once we know the instance lacks v2 grouping.
  if (getInstanceCapabilities().supportsNotificationsV2 === false) {
    return fetchV1NotificationGroups(params);
  }

  try {
    const response = await api().request<ApiNotificationGroupsResultJSON>({
      method: 'GET',
      url: '/api/v2/notifications',
      params,
    });

    const { statuses, accounts, notification_groups } = response.data;

    return {
      statuses,
      accounts,
      notifications: notification_groups,
      links: getLinks(response),
    };
  } catch (error: unknown) {
    if (
      error &&
      typeof error === 'object' &&
      'response' in error &&
      error.response &&
      typeof error.response === 'object' &&
      'status' in error.response
    ) {
      const status = error.response.status;

      if (status === 404 || status === 501) {
        setNotificationsV2Support(false);
        return fetchV1NotificationGroups(params);
      }
    }

    throw error;
  }
};

export const apiClearNotifications = () =>
  apiRequest<undefined>('POST', 'v1/notifications/clear');

export const apiFetchNotificationRequests = async (
  params?: {
    since_id?: string;
  },
  url?: string,
) => {
  const response = await api().request<ApiNotificationRequestJSON[]>({
    method: 'GET',
    url: url ?? '/api/v1/notifications/requests',
    params,
  });

  return {
    requests: response.data,
    links: getLinks(response),
  };
};

export const apiFetchNotificationRequest = async (id: string) => {
  return apiRequestGet<ApiNotificationRequestJSON>(
    `v1/notifications/requests/${id}`,
  );
};

export const apiAcceptNotificationRequest = async (id: string) => {
  return apiRequestPost(`v1/notifications/requests/${id}/accept`);
};

export const apiDismissNotificationRequest = async (id: string) => {
  return apiRequestPost(`v1/notifications/requests/${id}/dismiss`);
};

export const apiAcceptNotificationRequests = async (id: string[]) => {
  return apiRequestPost('v1/notifications/requests/accept', { id });
};

export const apiDismissNotificationRequests = async (id: string[]) => {
  return apiRequestPost('v1/notifications/requests/dismiss', { id });
};
